import React, { useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import UpcomingEmployeeEvents from '../components/UpcomingEmployeeEvents';
import { isRadarSourced } from '../../../constants/employmentSourceSystems';
import { contractActionFacts } from '../../../utils/contractActionFacts';

dayjs.extend(utc);

const EmployeeResignWithdraw = ({ employee, contract = null, episodeId = null, onValidChange }) => {
  // ADR-187 — the acting contract's own facts (passed by ContractActionModals);
  // the person's flat fields are the PRIMARY's and are read only through the
  // resolver, when this is the primary or the payload has no contracts[].
  const facts = contract || contractActionFacts(employee, episodeId ? { id: episodeId } : null);
  const [formData, setFormData] = useState({
    reason: ''
  });

  const [touched, setTouched] = useState({
    reason: false
  });

  const handleInputChange = (field) => (event) => {
    const newFormData = {
      ...formData,
      [field]: event.target.value
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Valid if reason is provided
    const isValid = event.target.value.trim().length > 0;
    onValidChange(isValid, newFormData);
  };

  const shouldShowError = (field) => {
    return touched[field];
  };

  React.useEffect(() => {
    // Initialize as invalid
    onValidChange(false, formData);
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity="info">
          This <strong>removes the scheduled end of employment</strong>: the pending
          termination is cancelled and the resignation date and reason are cleared. For a
          fixed-term contract this makes it <strong>open-ended</strong> — the person simply
          stays employed. To move the end instead, use <em>Change End Date</em>.
        </Alert>
        {/* ADR-146 §2 — warn (never block) on an ingestion-owned contract:
            the source stays authoritative, so an end date it still carries
            comes back on the next sync run. */}
        {!isRadarSourced({ source: facts.source }) && (
          <Alert severity="warning">
            This contract is <strong>owned by an ingestion source
            ({facts.source})</strong>. If the source system still carries
            an end date, the next sync run will <strong>restore it</strong> — remove it
            there too, or this withdrawal will not stick.
          </Alert>
        )}
        {/* ADR-136 §15 — fresh-fetched standing schedule (includes the end
            being removed, and anything else pending). Scoped to the acting
            contract when known (ADR-113 §8). */}
        <UpcomingEmployeeEvents employeeId={employee?.id} episodeId={episodeId} />
        {/* ADR-136 phase 6 (§15 slice 3 refinement): removing the end doesn't
            touch a scheduled replacement — the contract still ends at its cutover. */}
        {facts.pendingRenewalOn && (
          <Alert severity="info">
            A replacement is scheduled
            for <strong>{dayjs.utc(facts.pendingRenewalOn).format('DD/MM/YYYY')}</strong> —
            removing this end does not affect it: the contract will still end and be replaced
            on that date. Use <em>Cancel Scheduled Renewal</em> if the replacement is no
            longer wanted.
          </Alert>
        )}
        <TextField
          fullWidth
          label="Reason"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          multiline
          rows={4}
          required
          error={shouldShowError('reason') && !formData.reason}
          helperText={shouldShowError('reason') && !formData.reason
            ? 'Reason is required'
            : 'Reason for withdrawing the resignation'}
          placeholder="Enter reason for withdrawal..."
        />
      </Box>
    </Box>
  );
};

export default EmployeeResignWithdraw;
